import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { LeadTimeNote } from "@/components/shop/LeadTimeNote";
import { AgendaNote } from "@/components/agenda/AgendaNote";
import { useOrderGate } from "@/lib/agendaStore";
import { CartLine } from "@/components/shop/CartLine";
import { MessageOrder } from "@/components/shop/MessageOrder";
import { ShareCart } from "@/components/shop/ShareCart";
import { formatPrice } from "@/lib/format";
import { usePageMeta } from "@/lib/seo";

// Mensagem pré-escrita para o WhatsApp, com o que está no carrinho.
const cartMessage = (items) => ["Olá Alexandra! Gostava de fazer uma encomenda por mensagem 😊", ...items.map((i) => `${i.qty}x ${i.product.name}${i.pattern ? ` (${i.pattern.name})` : ""}`)].join("\n");

const Row = ({ label, value, testId, strong }) => (
  <div className={`flex items-baseline justify-between gap-4 ${strong ? "border-t border-ink/10 pt-4 text-base font-semibold" : "text-sm"}`}>
    <span>{label}</span>
    <span data-testid={testId} className="text-right">{value}</span>
  </div>
);

export default function CartPage() {
  const { items, subtotal, hasUnpriced, hasSoldOut, hasPhraseProblem, patterns } = useCart();
  const { blocked } = useOrderGate(items.map((i) => i.slug));
  usePageMeta("Carrinho", "Carrinho da loja Babybib.");
  const pending = hasUnpriced ? " + valores a confirmar" : "";
  return (
    <div className="bb-container pb-10 pt-10 max-lg:pb-28 sm:pt-16">
      <p className="bb-eyebrow mb-3 sm:mb-4">Carrinho</p>
      <h1 className="font-serif text-[2.6rem] font-normal leading-none tracking-tight sm:text-6xl" data-testid="cart-page-title">O teu carrinho</h1>
      {items.length === 0 ? (
        <div className="mt-12 rounded-3xl bg-linen p-10 text-center" data-testid="cart-page-empty">
          <p className="font-serif text-3xl">O carrinho está vazio.</p>
          <Link to="/loja" className="bb-btn-primary mt-8">Explorar a loja</Link>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-8 sm:mt-12 sm:gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7" data-testid="cart-page-items">
            {items.map((i) => <CartLine key={i.key} item={i} />)}
          </div>
          <aside className="h-fit space-y-4 rounded-3xl bg-linen p-5 sm:p-8 lg:sticky lg:top-24 lg:col-span-5" data-testid="cart-summary">
            <h2 className="font-serif text-2xl">Resumo</h2>
            <Row label="Portes" value="Grátis a partir de 100 €" testId="cart-summary-shipping" />
            <Row label="Subtotal (sem portes)" value={`${formatPrice(subtotal)}${pending}`} testId="cart-summary-total" strong />
            <LeadTimeNote variant="short" className="!bg-ivory" patterns={patterns} />
            <AgendaNote slugs={items.map((i) => i.slug)} />
            {blocked ? (
              <button type="button" disabled data-testid="cart-checkout-blocked" className="bb-btn-primary mt-2 w-full">Encomendas indisponíveis de momento</button>
            ) : hasSoldOut ? (
              <p className="rounded-xl bg-rose-soft px-4 py-3 text-xs text-bordeaux" role="alert" data-testid="cart-sold-out-note">Há peças com um padrão esgotado. Remove-as e escolhe outro padrão para finalizar a compra.</p>
            ) : hasPhraseProblem ? (
              <p className="rounded-xl bg-rose-soft px-4 py-3 text-xs text-bordeaux" role="alert" data-testid="cart-phrase-note">Há saquinhos cuja frase já não está disponível ou precisa de ser revista. Remove-os e personaliza-os de novo para finalizar a compra.</p>
            ) : hasUnpriced ? (
              <p className="rounded-xl bg-rose-soft px-4 py-3 text-xs text-bordeaux" data-testid="cart-unpriced-note">Há peças com preço a confirmar. Retira-as do carrinho para finalizar a compra.</p>
            ) : (
              <Link to="/finalizar" data-testid="cart-checkout-button" className="bb-btn-primary mt-2 w-full">Finalizar compra</Link>
            )}
            <MessageOrder testId="cart-message-order" message={cartMessage(items)} />
            <ShareCart items={items} subtotal={subtotal} />
            <p className="text-xs leading-relaxed text-taupe-strong" data-testid="cart-demo-note">
              Compra sem criar conta. Pagamento seguro na Stripe.
            </p>
          </aside>
        </div>
      )}
      {/* Telemóvel: o botão de finalizar fica sempre à vista, sem ter de descer até ao resumo. */}
      {items.length > 0 && !blocked && !hasSoldOut && !hasPhraseProblem && !hasUnpriced && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-ink/10 bg-ivory/95 px-4 py-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] backdrop-blur-xl lg:hidden" data-testid="cart-mobile-bar">
          <div className="mx-auto flex max-w-lg items-center gap-3">
            <div className="leading-tight"><p className="text-xs text-taupe">Subtotal</p><p className="text-base font-semibold">{formatPrice(subtotal)}</p></div>
            <Link to="/finalizar" data-testid="cart-checkout-button-mobile" className="bb-btn-primary ml-auto min-w-[11rem] flex-1">Finalizar compra</Link>
          </div>
        </div>
      )}
    </div>
  );
}
